import { ORIGINAL_EVENT_COMMAND_MANIFEST } from "../data/generated/original-event-command-manifest";
import {
  originalMsgSubArgs,
  originalMsgSubCommandName,
  originalMsgSubFields,
  originalMsgSubIsColonLabelLine,
  originalMsgSubTokens
} from "./originalMsgSubParser";

export type OriginalEventDispatchKind = "direct" | "queued_message_effect" | "label" | "unknown";

export type OriginalEventDispatch = {
  command: string;
  kind: OriginalEventDispatchKind;
  evidenceStatementIds: readonly string[];
};

type StatementRef = {
  statementId: string;
  statementText?: string;
};

type CommandCase = {
  caseAndBody: readonly StatementRef[];
};

const DECODED_INTERPRETER = ORIGINAL_EVENT_COMMAND_MANIFEST.decodedInterpreterRules;
const DIRECT_CASES = DECODED_INTERPRETER.commandDispatchRules.directCases as Record<string, CommandCase>;
const IF_CASES = DECODED_INTERPRETER.ifPredicateRules.cases as Record<string, readonly StatementRef[]>;

export const ORIGINAL_EVENT_COMMAND_RUNTIME_CONSUMPTION = {
  manifest: "docs/original-event-command-manifest.json",
  generatedManifestModule: "web/src/data/generated/original-event-command-manifest.ts",
  consumesSourceBackedEventCommands: true,
  sourceBackedStatus: ORIGINAL_EVENT_COMMAND_MANIFEST.status,
  sourceBackedCriteria: {
    sourceIrEventCommandProceduresDecoded:
      ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.sourceIrEventCommandProceduresDecoded,
    sourceBackedEventCommandStatementIds:
      ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.sourceBackedEventCommandStatementIds,
    msgSubCommandDispatchDecoded: ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.msgSubCommandDispatchDecoded,
    msgSubIfPredicatesDecoded: ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.msgSubIfPredicatesDecoded,
    msgSubJumpResolutionDecoded: ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.msgSubJumpResolutionDecoded,
    eventEntryDecoded: ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.eventEntryDecoded,
    messageQueuePrimitivesDecoded: ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.messageQueuePrimitivesDecoded,
    messageQueueCommandEffectsDecoded: ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.messageQueueCommandEffectsDecoded,
    messageQueueCommandEffectsScopedToMessageEffectsGate:
      ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.messageQueueCommandEffectsScopedToMessageEffectsGate,
    frmFullDecodeNotRequiredForEventCommandGate:
      ORIGINAL_EVENT_COMMAND_MANIFEST.criteria.frmFullDecodeNotRequiredForEventCommandGate
  },
  consumedStatementIds: {
    directDispatch: Object.fromEntries(
      Object.entries(DIRECT_CASES).map(([command, record]) => [command, statementIds(record.caseAndBody)])
    ),
    ifPredicates: Object.fromEntries(
      Object.entries(IF_CASES).map(([predicate, records]) => [predicate, statementIds(records)])
    ),
    eventEntry: statementIds(DECODED_INTERPRETER.eventEntryRules.msgSubCalls),
    jumpResolution: statementIds(DECODED_INTERPRETER.jumpResolutionRules.caseJDispatch),
    messageQueue: statementIds(DECODED_INTERPRETER.messageQueuePrimitiveRules.Message.timerAssignments)
  }
} as const;

export function originalEventCommandRuntimeConsumesSourceBackedEventCommands(): boolean {
  return ORIGINAL_EVENT_COMMAND_RUNTIME_CONSUMPTION.consumesSourceBackedEventCommands;
}

export function originalEventTokens(script: string): string[] {
  return originalMsgSubTokens(script);
}

export function originalEventLabels(tokens: readonly string[]): Map<string, number> {
  const labels = new Map<string, number>();
  tokens.forEach((token, index) => {
    const first = originalEventCommandParts(token)[0] ?? "";
    if (first.length > 0) labels.set(first.toUpperCase(), index);
  });
  return labels;
}

export function originalEventCommandName(token: string): string {
  return originalMsgSubCommandName(token);
}

export function originalEventArgs(token: string): string[] {
  return originalMsgSubArgs(token);
}

export function originalIfPredicateType(token: string): string {
  return (originalEventArgs(token)[0] ?? "").toUpperCase();
}

export function originalEventDispatch(command: string): OriginalEventDispatch {
  if (!command) return { command, kind: "unknown", evidenceStatementIds: [] };
  if (originalMsgSubIsColonLabelLine(command)) return { command, kind: "label", evidenceStatementIds: [] };
  const direct = DIRECT_CASES[command];
  if (direct) return { command, kind: "direct", evidenceStatementIds: statementIds(direct.caseAndBody) };
  return {
    command,
    kind: "queued_message_effect",
    evidenceStatementIds: statementIds(DIRECT_CASES.default?.caseAndBody ?? [])
  };
}

function statementIds(records: readonly StatementRef[]): readonly string[] {
  return records.map((record) => record.statementId).filter((statementId) => statementId.length > 0);
}

export function originalEventCommandParts(token: string): string[] {
  return originalMsgSubFields(token);
}
